import React, { useState, useEffect } from 'react';
import { coreValues, LOGO } from '../mock';
import { Target, Compass, Sparkles, Infinity as InfinityIcon } from 'lucide-react';
import useReveal from '../hooks/useReveal';

const TABS = [
  {
    key: 'mission',
    label: 'Mission',
    Icon: Target,
    accent: 'var(--teal-light)',
    title: 'Craft & Commitment',
    body: 'Deliver world-class corporate signage with craftsmanship & consistency — building lasting, long-term client relationships.',
  },
  {
    key: 'vision',
    label: 'Vision',
    Icon: Compass,
    accent: 'var(--orange)',
    title: 'Global Trust',
    body: 'A globally trusted brand known for premium signage, seamless execution & customer-first service across major markets.',
  },
  {
    key: 'values',
    label: 'Core Values',
    Icon: Sparkles,
    accent: 'var(--teal-light)',
    title: 'What Drives Us',
    body: 'Excellence, integrity, consistency, innovation and customer focus — five principles that guide every project from concept to install.',
  },
  {
    key: 'logo',
    label: 'The Mark',
    Icon: InfinityIcon,
    accent: 'var(--orange)',
    title: 'Crafting Visual Impact',
    body: 'The Vistora infinity mark represents the perpetual pursuit of visual craft — an unbroken loop of design, fabrication, and installation excellence.',
  },
];

export default function Overview() {
  const [active, setActive] = useState(0);
  const [ref, inView] = useReveal(0.1);

  // Auto-rotate every 5s
  useEffect(() => {
    if (!inView) return;
    const id = setInterval(() => setActive((a) => (a + 1) % TABS.length), 5500);
    return () => clearInterval(id);
  }, [inView]);

  const current = TABS[active];

  return (
    <section ref={ref} className="section section-paper paper-tex" style={{ overflow: 'hidden' }}>
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left title */}
          <div className="lg:col-span-5">
            <div className={`reveal from-left ${inView ? 'in-view' : ''}`}>
              <div className="eyebrow" style={{ marginBottom: 24 }}>§ 01 · Company Overview</div>
              <h2 className="section-title">Premium <em>visual</em> branding &amp; <span className="orange">installations</span>.</h2>
              <p className="font-serif-i" style={{ fontSize: 18, color: 'var(--ink-3)', marginTop: 28, lineHeight: 1.6, maxWidth: 460 }}>
                Total turnkey corporate signage and installation — quality-driven, relationship-first, delivered globally.
              </p>
              <div className="font-mono" style={{ color: 'var(--muted)', fontSize: 11, letterSpacing: '0.22em', marginTop: 20, textTransform: 'uppercase' }}>
                Vistora Branding Solutions Pvt. Ltd.
              </div>
            </div>
          </div>

          {/* Right: Interactive tabbed panel */}
          <div className="lg:col-span-7">
            <div className={`reveal from-right ${inView ? 'in-view' : ''}`}>
              {/* Tab bar */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
                {TABS.map((t, i) => {
                  const isActive = i === active;
                  const IconEl = t.Icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setActive(i)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '10px 16px',
                        border: `1px solid ${isActive ? 'var(--ink)' : 'rgba(31,42,46,0.2)'}`,
                        background: isActive ? 'var(--ink)' : 'transparent',
                        color: isActive ? 'var(--paper)' : 'var(--ink)',
                        borderRadius: 999,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(.2,.8,.2,1)',
                      }}
                    >
                      <IconEl size={12} strokeWidth={2} />
                      <span className="font-mono" style={{ color: isActive ? t.accent : 'var(--muted)', marginRight: 4 }}>0{i + 1}</span>
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Animated panel */}
              <div style={{ position: 'relative', minHeight: 460 }}>
                {TABS.map((t, i) => {
                  const isActive = i === active;
                  const IconEl = t.Icon;
                  return (
                    <div
                      key={t.key}
                      style={{
                        position: 'absolute', inset: 0,
                        opacity: isActive ? 1 : 0,
                        transform: isActive ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
                        transition: 'opacity 0.55s cubic-bezier(.2,.8,.2,1), transform 0.55s cubic-bezier(.2,.8,.2,1)',
                        pointerEvents: isActive ? 'auto' : 'none',
                      }}
                    >
                      {t.key === 'logo' ? (
                        <AnimatedLogoPanel />
                      ) : t.key === 'values' ? (
                        <ValuesPanel />
                      ) : (
                        <ContentPanel Icon={IconEl} accent={t.accent} title={t.title} body={t.body} num={i + 1} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Progress dots */}
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                {TABS.map((_, i) => (
                  <div key={i} style={{
                    height: 3, width: i === active ? 40 : 18,
                    background: i === active ? 'var(--ink)' : 'rgba(31,42,46,0.2)',
                    borderRadius: 2,
                    transition: 'all 0.4s',
                    cursor: 'pointer',
                  }}
                    onClick={() => setActive(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Standard content panel with big number + icon + copy */
function ContentPanel({ Icon, accent, title, body, num }) {
  return (
    <div className="art-frame" style={{ width: '100%' }}>
      <div style={{ background: 'var(--ink)', padding: '48px 44px', minHeight: 420, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', bottom: -60, right: -40,
          fontFamily: "'Fraunces', serif", fontWeight: 900,
          fontSize: 280, lineHeight: 1, color: 'rgba(255,255,255,0.03)',
        }}>0{num}</div>

        <div style={{
          width: 62, height: 62,
          border: `1px solid ${accent}55`,
          background: `${accent}12`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 32,
          borderRadius: 6,
        }}>
          <Icon size={26} color={accent} strokeWidth={1.6} />
        </div>

        <div className="font-mono" style={{ color: accent, fontSize: 11, letterSpacing: '0.24em', marginBottom: 14 }}>
          {String(num).padStart(2, '0')} / 04
        </div>
        <h3 className="font-display" style={{ color: 'var(--paper)', fontSize: 40, letterSpacing: '-0.02em', marginBottom: 18, lineHeight: 1 }}>
          {title}
        </h3>
        <p style={{ color: 'var(--smoke)', fontSize: 15, lineHeight: 1.7, maxWidth: 520 }}>{body}</p>
      </div>
    </div>
  );
}

/* Core Values panel - animated chips */
function ValuesPanel() {
  return (
    <div className="art-frame" style={{ width: '100%' }}>
      <div style={{ background: 'var(--teal)', padding: '48px 44px', minHeight: 420, position: 'relative', overflow: 'hidden' }}>
        <div className="font-mono" style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, letterSpacing: '0.24em', marginBottom: 14 }}>
          03 / 04
        </div>
        <h3 className="font-display" style={{ color: 'var(--warm-white)', fontSize: 40, letterSpacing: '-0.02em', marginBottom: 14, lineHeight: 1 }}>
          What Drives Us
        </h3>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, lineHeight: 1.6, marginBottom: 26, maxWidth: 520 }}>
          Five principles guide every project from concept sketches to on-site installation.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {coreValues.map((v, i) => (
            <span key={v} style={{
              padding: '10px 18px',
              border: '1px solid var(--warm-white)',
              color: 'var(--warm-white)',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
              borderRadius: 999,
              opacity: 0,
              transform: 'translateY(10px)',
              animation: `chipIn 0.5s cubic-bezier(.2,.8,.2,1) ${0.15 + i * 0.1}s forwards`,
            }}>
              <span style={{ color: 'var(--orange)', marginRight: 8 }}>0{i + 1}</span>{v}
            </span>
          ))}
        </div>

        <style>{`@keyframes chipIn { to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    </div>
  );
}

/* Animated Vistora logo panel */
function AnimatedLogoPanel() {
  return (
    <div className="art-frame" style={{ width: '100%' }}>
      <div style={{
        background: 'radial-gradient(ellipse at center, #14282c 0%, var(--ink) 70%)',
        padding: '48px 44px', minHeight: 420, position: 'relative', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 30,
      }}>
        {/* Rotating orbit ring */}
        <div style={{ position: 'relative', width: 260, height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Outer ring */}
          <div style={{
            position: 'absolute', inset: 0,
            border: '1px dashed rgba(79, 191, 184, 0.35)',
            borderRadius: '50%',
            animation: 'spin 22s linear infinite',
          }} />
          {/* Inner ring */}
          <div style={{
            position: 'absolute', inset: 30,
            border: '1px dashed rgba(232, 116, 53, 0.35)',
            borderRadius: '50%',
            animation: 'spin 14s linear infinite reverse',
          }} />
          {/* Orbit dot teal */}
          <div style={{ position: 'absolute', inset: 0, animation: 'spin 8s linear infinite' }}>
            <div style={{ position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', width: 12, height: 12, borderRadius: '50%', background: 'var(--teal-light)', boxShadow: '0 0 14px var(--teal-light)' }} />
          </div>
          {/* Orbit dot orange */}
          <div style={{ position: 'absolute', inset: 30, animation: 'spin 10s linear infinite reverse' }}>
            <div style={{ position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', width: 10, height: 10, borderRadius: '50%', background: 'var(--orange)', boxShadow: '0 0 14px var(--orange)' }} />
          </div>

          {/* Logo card with pulse */}
          <div style={{
            width: 150, height: 150, borderRadius: 16,
            background: 'var(--warm-white)',
            padding: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 20px 40px -10px rgba(0,0,0,0.8), 0 0 40px -8px rgba(79, 191, 184, 0.55)',
            animation: 'logoPulse 3s ease-in-out infinite',
          }}>
            <img src={LOGO} alt="Vistora" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div className="font-mono" style={{ color: 'var(--orange)', fontSize: 11, letterSpacing: '0.28em', marginBottom: 10 }}>04 / 04 · THE MARK</div>
          <div className="font-display" style={{ color: 'var(--paper)', fontSize: 30, letterSpacing: '-0.02em' }}>Crafting Visual Impact</div>
          <div className="font-serif-i" style={{ color: 'var(--smoke)', fontSize: 14, marginTop: 8 }}>An unbroken loop of design, fabrication &amp; installation.</div>
        </div>

        <style>{`
          @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
          @keyframes logoPulse {
            0%, 100% { transform: scale(1); box-shadow: 0 20px 40px -10px rgba(0,0,0,0.8), 0 0 40px -8px rgba(79, 191, 184, 0.55); }
            50% { transform: scale(1.05); box-shadow: 0 20px 60px -6px rgba(0,0,0,0.8), 0 0 60px -4px rgba(232, 116, 53, 0.7); }
          }
        `}</style>
      </div>
    </div>
  );
}
